<template>
  <div
    class="h-full z-40 sm:w-[27rem] w-full pb-safe pt-safe sm:absolute fixed right-0 top-0 bg-n-800 border-l border-n-700 flex flex-col"
  >
    <div class="px-2 pt-2 flex">
      <l-button icon="close" size="small" type="text" class="ml-auto" @click="$emit('close')" />
    </div>
    <div class="flex flex-col h-full gap-6 px-4 pb-4">
      <div class="flex items-center gap-3 min-w-0 text-n-0">
        <div
          class="h-16 w-16 rounded-full bg-r-400 font-body text-2xl text-n-0 flex items-center justify-center uppercase"
        >
          {{ initialName }}
        </div>
        <div class="flex flex-col flex-1 min-w-0">
          <div
            class="font-heading font-bold text-md text-ellipsis overflow-hidden whitespace-nowrap capitalize"
          >
            {{ reservation.name }}
            {{ reservation.surname }}
          </div>
          <div>
            {{ phoneNumber }}
          </div>
          <div v-if="reservation.email" class="text-sm truncate text-n-200">
            {{ reservation.email }}
          </div>
        </div>
      </div>
      <div class="border-t border-n-700"></div>
      <div class="flex flex-col gap-2 text-n-0">
        <div class="grid grid-cols-3 gap-2">
          <div class="h-6 flex items-center gap-1">
            <l-icon name="people" />
            {{ reservation.diners }}
          </div>
          <div class="h-6 flex items-center gap-1">
            <l-icon name="time" />
            {{ format(reservation.dateTime, 'HH:mm') }}
          </div>
          <div class="h-6 flex items-center gap-1 min-w-0">
            <l-icon name="floorplan" />
            <span class="truncate">{{ floorplan }}</span>
          </div>
          <div class="h-6 flex items-center gap-1 min-w-0">
            <l-icon name="table" />
            <span class="truncate">{{ tables }}</span>
          </div>
          <div class="h-6 flex items-center gap-1">
            <l-icon name="location" />
            {{ source }}
          </div>
          <div class="h-6 flex items-center gap-1">
            <div v-if="reservation.status === 'no-show'">
              <l-status type="secondary" status="error" size="small" :show-icon="false">
                {{ t('reservations.no-show') }}
              </l-status>
            </div>
          </div>
        </div>
        <div class="flex flex-col gap-1" v-if="reservation.customerComments">
          <div class="h-6 flex items-center gap-1">
            <l-icon name="chat" />
            {{ t('reservations.comments-label') }}
          </div>
          <div class="text-n-300">
            {{ reservation.customerComments }}
          </div>
        </div>
      </div>
      <div v-if="hasWarranty" class="border-t border-n-700">
        <div class="flex items-center gap-2 mt-4 text-n-0">
          <l-icon name="refund" />
          <div v-if="reservation.policy === 'card'">
            {{ t('reservations.policy-card') }}
          </div>
          <div v-if="reservation.policy === 'deposit'">
            {{ t('reservations.policy-deposit') }}
          </div>
        </div>
        <div class="text-n-300">
          {{
            reservation.policyMethod === 'by-person'
              ? t('reservations.per-guest')
              : t('reservations.per-reservation')
          }}
          -
          {{ currency(reservation.policyAmount) }}
        </div>
      </div>

      <div
        v-if="reservation.status === 'finished'"
        class="flex flex-col gap-2 border-t border-n-700 text-sm pt-4"
      >
        <div class="flex gap-2 items-center text-n-0">
          <l-icon name="ticket" />
          <div>{{ t('reservations.tab-summary') }}</div>
        </div>
        <div class="flex justify-between text-n-300">
          <div class="flex flex-col gap-2">
            <div>{{ t('reservations.payments') }}</div>
            <div v-for="(payment, index) in payments" :key="index" class="capitalize">
              {{ `${payment.type} - ${currency(payment.amount)}` }}
            </div>
          </div>
          <div class="flex flex-col gap-2">
            <div>{{ t('reservations.duration') }}</div>
            <div>{{ getReservationDuration }}</div>
          </div>
        </div>
      </div>
      <div class="flex gap-2 mt-auto">
        <l-button
          v-if="primaryButton"
          class="w-full"
          size="small"
          type="primary"
          :loading="loading"
          @click="primaryButton.action()"
        >
          {{ primaryButton.text }}
        </l-button>
        <l-dropdown
          v-if="
            !(
              props.reservation.status === 'no-show' &&
              props.reservation.policy === 'deposit' &&
              !props.reservation.policyCharged
            ) && options.length > 0
          "
          v-model:open="openOptions"
          :options="options"
          :mark-selected="false"
          placement="bottom-end"
        >
          <l-button
            icon="3-dots"
            size="small"
            type="secondary"
            class="text-v-300 w-full p-2"
            @click.stop="openOptions = true"
          />
        </l-dropdown>
      </div>
    </div>
    <table-selector
      v-model:is-active="selectingTable"
      :title="t('reservations.title')"
      :button-text="t('reservations.accept')"
      :blocked-tables="getBlockedTables({ reservationId: reservation.id })"
      :multiple-select="true"
      :selected-tables="reservation.tables"
      :show-actual-status="false"
      @close="selectingTable = false"
      @tables-selected="updateTables"
    />
  </div>
</template>

<script setup lang="ts">
import { differenceInMinutes, format } from 'date-fns'
import parsePhoneNumber from 'libphonenumber-js'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import { LButton, LDropdown, LIcon, LStatus, useDialog } from '@last/core-ui/paprika'
import type { Option } from '@last/core-ui/paprika/components/dropdown/types'

import { reservationsApi } from '@/api/client'
import { useMoney } from '@/composables/useMoney'
import { useNotifications } from '@/composables/useNotifications'
import { useReservations } from '@/composables/useReservations'
import { useTabs } from '@/composables/useTabs'
import TableSelector from '@/modules/floorplan/TableSelector.vue'
import { useFloorplanStore } from '@/store/floorplan'
import { useReservationsStore } from '@/store/reservations'
import { useTabsStore } from '@/store/tabs'
import type { Reservation } from '@/types'

const { t } = useI18n()
const router = useRouter()
const dialog = useDialog()
const { currency } = useMoney()
const { notifySuccess, notifyError } = useNotifications()

const props = defineProps<{
  reservation: Reservation
}>()

const emit = defineEmits<{
  close: []
  edit: []
  changeTable: []
}>()

const openOptions = ref(false)
const selectingTable = ref(false)
const loading = ref(false)

const { hasWarranty } = useReservations(props.reservation.id)
const { editReservation, getBlockedTables } = useReservationsStore()
const { openTabWithCustomer } = useTabsStore()

const phoneNumber = computed(() => {
  return props.reservation.phoneNumber
    ? parsePhoneNumber(props.reservation.phoneNumber)?.formatInternational()
    : null
})

const initialName = computed(() => {
  const fullName = `${props.reservation.name} ${props.reservation.surname}`
  const initials = fullName
    .split(/[.,/ -]/)
    .map(n => n[0])
    .join('')
    .slice(0, 2)
  if (initials.length < 2) {
    return fullName.slice(0, 2)
  } else {
    return initials
  }
})

const floorplanStore = useFloorplanStore()
const { tables: allTables } = storeToRefs(floorplanStore)

const tables = computed(() => {
  const { tables, zone } = props.reservation
  if (tables && tables.length) {
    return tables.map(table => allTables.value[table].name).join(', ')
  }
  return zone || '-'
})

const floorplan = computed(() => {
  const { tables } = props.reservation
  if (tables && tables.length) {
    return floorplanStore.getTableFloorplan(tables[0]).name
  }
  return '-'
})

const source = computed(() => {
  const source = props.reservation.source
  if (source && !['pos', 'admin'].includes(source)) {
    return props.reservation.source
  }
  return t('reservations.restaurant')
})

const options = computed(() => {
  const options: Option[] = []

  if (props.reservation.status === 'confirmed') {
    options.push({
      label: t('reservations.change-table'),
      value: 'change-table',
      onClick: () => (selectingTable.value = true)
    })
    if (isOnTime.value) {
      options.push({
        label: t('reservations.mark-as-no-show'),
        value: 'no-show',
        onClick: () => markNoShow()
      })
      options.push({
        label: t('reservations.edit'),
        value: 'edit',
        onClick: () => emit('edit')
      })
    }
    if (isLate.value) {
      options.push({
        label: t('reservations.edit'),
        value: 'edit',
        onClick: () => emit('edit')
      })
    }
    options.push({
      label: t('reservations.delete-reservation'),
      value: 'cancel-reservation',
      onClick: () => cancel()
    })
  }
  if (props.reservation.status === 'ongoing') {
    options.push({
      label: t('reservations.view-tab'),
      value: 'view-tab',
      onClick: () => viewTab()
    })
  }
  if (
    props.reservation.status === 'no-show' &&
    (props.reservation.policy === 'card' || props.reservation.policyCharged)
  ) {
    options.push({
      label: t('reservations.undo-no-show'),
      value: 'undo-no-show',
      onClick: () => undoNoShow()
    })
  }
  if (props.reservation.status === 'finished') {
    options.push({
      label: t('reservations.view-payments'),
      value: 'view-payments',
      onClick: () =>
        router.push({
          name: 'checkout',
          params: {
            tabId: props.reservation.tabId
          }
        })
    })
  }

  return options
})

const getReservationDuration = computed(() => {
  if (!props.reservation.endTime || !props.reservation.tabId) return ''
  const tab = useTabs(props.reservation.tabId).tab.value
  const diffInMinutes = differenceInMinutes(
    new Date(props.reservation.endTime),
    new Date(tab.creationTime)
  )

  const hours = Math.floor(diffInMinutes / 60)
  const minutes = diffInMinutes % 60
  return `${hours > 0 ? `${hours}h ` : ''}${minutes >= 0 ? `${minutes}m` : ''}`.trim()
})

const isOnTime = computed(() => {
  const now = new Date()
  const reservationTime = new Date(props.reservation.dateTime)
  const diffInMinutes = differenceInMinutes(reservationTime, now)
  return diffInMinutes <= 60 && diffInMinutes >= -15 // Allow 15 minutes grace period
})

const isLate = computed(() => {
  const now = new Date()
  const reservationTime = new Date(props.reservation.dateTime)
  return differenceInMinutes(now, reservationTime) > 15 // More than 15 minutes late
})

function goToOrderManager() {
  router.push({
    name: 'orderManagement',
    params: {
      tabId: props.reservation.tabId
    }
  })
}

function createTab() {
  const reservation = props.reservation
  const tabId = openTabWithCustomer({
    tableIds: reservation.tables,
    tab: {
      seats: reservation.diners
    },
    customer: {
      name: reservation.name,
      surname: reservation.surname ?? '',
      phoneNumber: reservation.phoneNumber,
      email: reservation.email
    },
    reservationId: reservation.id
  })
  editReservation({ ...props.reservation, tabId, status: 'ongoing' })
  router.push({ name: 'orderManagement', params: { tabId } })
}

function markNoShow() {
  editReservation({ ...props.reservation, status: 'no-show' })
}

function undoNoShow() {
  editReservation({ ...props.reservation, status: 'confirmed' })
}

function cancel() {
  dialog({
    title: t('reservations.delete-reservation'),
    content: t('reservations.delete-reservation-content', {
      diners: props.reservation.diners,
      name: props.reservation.name,
      date: format(props.reservation.dateTime, 'dd MMMM'),
      time: format(props.reservation.dateTime, 'HH:mm')
    }),
    mainLabel: t('reservations.confirm-cancel'),
    secondaryLabel: t('ctas.no'),
    icon: 'calendar',
    onConfirm: async () => {
      await reservationsApi.cancelReservation({
        reservationId: props.reservation.id
      })
      emit('close')
    }
  })
}

function viewTab() {
  router.push({
    name: 'toGoTabs',
    query: {
      preSelectedTabId: props.reservation.tabId
    }
  })
}

async function updateTables(tables: string[]) {
  selectingTable.value = false
  const body = {
    id: props.reservation.id,
    dateTime: props.reservation.dateTime,
    diners: props.reservation.diners,
    tables,
    zone: props.reservation.zone,
    name: props.reservation.name,
    surname: props.reservation.surname!,
    phoneNumber: props.reservation.phoneNumber,
    customerComments: props.reservation.customerComments!
  }
  await reservationsApi.updateReservation({
    createReservationRequest: body,
    reservationId: props.reservation.id
  })
}

async function chargePolicy() {
  loading.value = true
  await reservationsApi.chargePolicy({
    reservationId: props.reservation.id
  })
  editReservation({ ...props.reservation, policyCharged: true })
  notifySuccess({
    title: t('reservations.policy-charge-success')
  })
  loading.value = false
}

async function refundPolicy() {
  loading.value = true
  try {
    await reservationsApi.refundPolicy({
      reservationId: props.reservation.id
    })
    editReservation({ ...props.reservation, policyCharged: false })
    notifySuccess({
      title: t('reservations.warranty-refunded-success')
    })
  } catch (_) {
    notifyError({
      title: t('reservations.warranty-refund-error')
    })
  }
  loading.value = false
}

const payments = computed(() => {
  if (props.reservation.tabId) {
    return useTabs(props.reservation.tabId).allPayments.value
  }
  return []
})

const primaryButton = computed(() => {
  const { reservation } = props

  if (reservation.status === 'ongoing') {
    return {
      text: t('reservations.add-products'),
      action: goToOrderManager
    }
  }

  if (isOnTime.value && reservation.status === 'confirmed') {
    return {
      text: t('reservations.open-tab'),
      action: createTab
    }
  }

  if (isLate.value && reservation.status === 'confirmed') {
    return {
      text: t('reservations.mark-as-no-show'),
      action: markNoShow
    }
  }

  if (reservation.status === 'confirmed') {
    return {
      text: t('reservations.edit'),
      action: () => emit('edit')
    }
  }

  if (reservation.status === 'finished') {
    return {
      text: t('reservations.view-tab'),
      action: viewTab
    }
  }

  if (
    reservation.status === 'no-show' &&
    reservation.policy === 'card' &&
    !reservation.policyCharged
  ) {
    return {
      text: t('reservations.charge-hold'),
      action: chargePolicy
    }
  }

  if (
    reservation.status === 'no-show' &&
    reservation.policy === 'deposit' &&
    !reservation.policyCharged
  ) {
    return {
      text: t('reservations.undo-no-show'),
      action: () => editReservation({ ...reservation, status: 'confirmed' })
    }
  }

  if (reservation.status === 'no-show' && reservation.policyCharged) {
    return {
      text: t('reservations.refund-warranty'),
      action: refundPolicy
    }
  }

  if (reservation.status === 'no-show') {
    return {
      text: t('reservations.undo-no-show'),
      action: undoNoShow
    }
  }

  return null
})
</script>
